import { ButtonProps } from '@almbrand/button';
import { ModalProps as BaseModalProps } from '@almbrand/modal';
import classNames from 'classnames';
import { useAppDispatch } from 'store/hooks';
import { MobileVariation, setModal } from 'store/slices/modalSlice';
import { replacePlaceholdersModal } from '../services/PlaceholderService';

type ActionButtonProps = {
	title: string;
	type?: ButtonProps['buttonType'];
	onClick?: (e?: Event) => void;
};

export type DisplayModalOptions = {
	title: string;
	subtitle: string;
	content: string | React.ReactNode;
	fields?: any[] | null;
	actionButtonProps?: ActionButtonProps | null;
	className?: string | null;
	mobileVariation?: MobileVariation | null;
	flowStop?: boolean;
	padding?: BaseModalProps['padding'];
	cssStyle?: React.CSSProperties;
};

const getReplaceTokens = (title: string, subtitle: string, content: string) => {
	const replaceTokens = {
		title,
		subtitle,
		content,
		contentType: 'ModalContentBlock',
	};
	replacePlaceholdersModal(replaceTokens);
	return replaceTokens;
};

const getPreviousFocusElementId = (): string | undefined => {
	const activeElement = document.activeElement as HTMLElement;
	if (!activeElement) return undefined;

	// Generate a unique selector for the element
	if (activeElement.id) return `#${activeElement.id}`;

	// If no id, try to generate a data attribute we can use
	const timestamp = Date.now();
	const dataAttr = `data-focus-restore-${timestamp}`;
	activeElement.setAttribute(dataAttr, 'true');
	return `[${dataAttr}="true"]`;
};
export const useModal = () => {
	const dispatch = useAppDispatch();

	const displayErrorModal = (title: string, subtitle: string, content: string, fields?: any[]) => {
		if (title) {
			const replaceTokens = getReplaceTokens(title, subtitle, content);
			const previousFocusElement = getPreviousFocusElementId();
			dispatch(
				setModal({
					displayModal: true,
					modalProps: {
						title: replaceTokens.title,
						subtitle: replaceTokens.subtitle,
						fields,
						content: replaceTokens.content,
						isErrorModal: true,
					},
					previousFocusElement,
				})
			);
		}
	};

	const displayModal = ({
		title,
		subtitle,
		content,
		fields,
		actionButtonProps,
		className,
		mobileVariation,
		flowStop,
		padding,
		cssStyle,
	}: DisplayModalOptions) => {
		let reactContent: React.ReactNode;
		if (content && typeof content !== 'string') {
			reactContent = content;
		}
		const replaceTokens = getReplaceTokens(title, subtitle, reactContent ? '' : (content as string));
		const previousFocusElement = getPreviousFocusElementId();

		dispatch(
			setModal({
				displayModal: true,
				modalProps: {
					title: replaceTokens.title,
					subtitle: replaceTokens.subtitle,
					content: reactContent || replaceTokens.content,
					fields: fields ?? undefined,
					actionButtonProps: actionButtonProps ?? undefined,
					className: className ? classNames(className) : undefined,
					mobileVariation: mobileVariation ?? undefined,
					flowStop,
					padding,
				},
				previousFocusElement,
			})
		);

		return cssStyle;
	};

	return { displayErrorModal, displayModal };
};
